import DatePicker from 'components/base/DatePicker';
import React from 'react';
import { Col, Row } from 'react-bootstrap';
import { Form } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import useFetchSupplier from './useFetchSupplier';
import axiosInstance from 'utils/axiosInterceptors';
import { useBranchContext } from 'providers/BranchProvider';
import { formatToUTCISO } from 'utils/formatToUTCISO';
import { toast } from 'react-toastify';
import { toastConfig } from 'utils/toastConfig';

type TSupplierPaymentDetails = {
  supplierPaymentMethod: string;
  accountDate: Date | string;
  supplierId: number;
  procurementInvoiceIds: (number | undefined)[];
  procurementReturnIds: (number | undefined)[];
  paymentNotes?: string;
};

export type TCreateSupplierPaymentFormProps = {
  submitForm: () => void;
};

const CreateSupplierPaymentForm =
  React.forwardRef<TCreateSupplierPaymentFormProps>((_, ref) => {
    const { suppliers } = useFetchSupplier();
    const { selectedBranch } = useBranchContext();
    const { register, handleSubmit, setValue, watch } =
      useForm<TSupplierPaymentDetails>({
        defaultValues: {
          supplierPaymentMethod: '',
          accountDate: new Date(),
          supplierId: 0,
          procurementInvoiceIds: [],
          procurementReturnIds: [],
          paymentNotes: ''
        }
      });

    const accountDate = watch('accountDate');

    const onSubmit = async (data: TSupplierPaymentDetails) => {
      const formattedDate = formatToUTCISO(data.accountDate);
      const formattedPaymentDetails = {
        ...data,
        accountDate: formattedDate
      };
      try {
        await axiosInstance.post(
          `/api/v1/${selectedBranch?.branchDomain}/supplierPayments`,
          formattedPaymentDetails
        );
        toast.success('Payment details submitted successfully!', {
          ...toastConfig,
          autoClose: 2000
        });
      } catch (error) {
        console.error(error);
        toast.error('Failed to submit payment details. Please try again.');
      }
    };

    React.useImperativeHandle(ref, () => ({
      submitForm: () => handleSubmit(onSubmit)()
    }));

    return (
      <>
        <Form className="w-100">
          <Row className="mb-3">
            <Form.Group as={Col} controlId="paymentMethod">
              <Form.Label>Payment Method</Form.Label>
              <Form.Check
                type="radio"
                id="checkRadio"
                label="Check"
                value="Check"
                {...register('supplierPaymentMethod')}
              />
              <Form.Check
                type="radio"
                id="cashRadio"
                label="Cash"
                value="Cash"
                {...register('supplierPaymentMethod')}
              />
              <Form.Check
                type="radio"
                id="bankTransferRadio"
                label="Bank Transfer"
                value="BankTransfer"
                {...register('supplierPaymentMethod')}
              />
            </Form.Group>
          </Row>
          <Row className="mb-3">
            <Form.Group as={Col} controlId="accountDate">
              <Form.Label>Account Date</Form.Label>
              <DatePicker
                value={
                  accountDate instanceof Date
                    ? accountDate
                    : new Date(accountDate)
                }
                onChange={date => setValue('accountDate', (date as Date[])[0])}
              />
            </Form.Group>
          </Row>
          <Row className="mb-3">
            <Form.Group as={Col} controlId="paymentNotes">
              <Form.Label>Remarks</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                {...register('paymentNotes')}
              />
            </Form.Group>
          </Row>
          <Row className="mb-3">
            <Form.Group as={Col} controlId="supplierId">
              <Form.Label>Select Supplier</Form.Label>
              <Form.Select {...register('supplierId')}>
                <option value="">Select Supplier</option>
                {suppliers.map(supplier => (
                  <option key={supplier.supplierId} value={supplier.supplierId}>
                    {supplier.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Row>
        </Form>
      </>
    );
  });

export default CreateSupplierPaymentForm;
